import { useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import { EyeOff, ScanEye } from 'lucide-react'

import { useApi } from '@fortem-monorepo/react-spa'
import {
  LoadingScreen,
  SubmitButton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@fortem-monorepo/react-ui'
import { Button } from '@fortem-monorepo/react-ui'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@fortem-monorepo/react-ui'
import { ICG2Guidelines } from '@fortem-monorepo/web-sdk'

import { useUserContext } from '@/index'
import { useGuidelineDrawer } from '@/pages/funds/ic-guidelines-v2/components/GuidelineDrawerContext'
import { ImagePreview } from '@/pages/funds/ic-guidelines-v2/components/ImagePreview'
import { EditGuidelineForm } from '@/pages/funds/ic-guidelines-v2/components/edit-guideline-form/EditGuidelineForm'
import { EditGuidelineFormFields } from '@/pages/funds/ic-guidelines-v2/components/edit-guideline-form/EditGuidelineFormFields'
import { FormValues } from '@/pages/funds/ic-guidelines-v2/components/edit-guideline-form/FormValues'

export interface GuidelineDrawerProps {}

export const GuidelineDrawer: React.FC<GuidelineDrawerProps> = ({}) => {
  const { drawer, setDrawer } = useGuidelineDrawer()
  const [formValuesOpen, setFormValuesOpen] = useState(false)

  const { isDeveloper } = useUserContext()

  const close = () => {
    setDrawer({
      open: false,
      mode: 'create',
    })
  }

  const api = useApi()

  const guidelinesQuery = useQuery({
    queryKey: ['ic-guidelines', 'guidelines'],
    queryFn: () => api.icGuidelines.v2.getGuidelines(),
    enabled: !!drawer.guidelineId,
  })

  const guideline = guidelinesQuery.data?.find((g) => g.id === drawer.guidelineId)

  const guidelineFormValues: ICG2Guidelines.Guideline | undefined = guideline
    ? {
        id: guideline.id,
        name: guideline.name,
        description: guideline.description,
        table: guideline.table,
        funds: guideline.funds,
        value_column: guideline.value_column,
        aggregation_function: guideline.aggregation_function,
        threshold_type: guideline.threshold_type,
        identifiers: guideline.identifiers,
        thresholds: guideline.thresholds,
        is_active: guideline.is_active,
      }
    : undefined

  return (
    <Sheet open={drawer.open} onOpenChange={(open) => setDrawer((prev) => ({ ...prev, open }))}>
      <SheetContent className="max-w-full sm:max-w-4xl gap-0">
        {guidelinesQuery.isLoading && <LoadingScreen />}
        <EditGuidelineForm defaultValues={guidelineFormValues} mode={drawer.mode}>
          {({ formState: { isValid, isDirty, isSubmitting } }) => {
            const canMakeChanges = isValid && isDirty
            const canSubmit = canMakeChanges && !isSubmitting

            return (
              <>
                <SheetHeader className={'border-b'}>
                  <SheetTitle>
                    {drawer.mode === 'edit'
                      ? `Edit Guideline${guideline?.name ? ` | ${guideline.name}` : ''}`
                      : 'Add Guideline'}
                  </SheetTitle>
                </SheetHeader>
                <div className="flex flex-col p-4 gap-4 overflow-y-auto bg-sidebar h-full">
                  <EditGuidelineFormFields />
                  <ImagePreview />
                  {formValuesOpen && <FormValues />}
                </div>
                <SheetFooter className={'border-t'}>
                  <div className="flex items-center gap-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <SubmitButton
                          loading={isSubmitting}
                          size="sm"
                          disabled={!canSubmit}
                          variant={canMakeChanges ? 'success' : 'default'}
                        >
                          Submit
                        </SubmitButton>
                      </TooltipTrigger>
                      <TooltipContent className={'max-w-100'}>
                        This will reset the guideline's status to 'PENDING'. You will need to{' '}
                        <strong>regain</strong> approval for the guideline for it to remain active
                        in reports.
                      </TooltipContent>
                    </Tooltip>
                    <Button type="button" variant={'destructive'} size="sm" onClick={() => close()}>
                      Cancel
                    </Button>
                    {isDeveloper() && (
                      <Button
                        type="button"
                        variant={'outline'}
                        size="sm"
                        onClick={() => setFormValuesOpen((prev) => !prev)}
                      >
                        {!formValuesOpen ? <ScanEye /> : <EyeOff />}
                      </Button>
                    )}
                    {isDirty && drawer.mode === 'edit' && (
                      <span className={'font-medium text-red-500 text-sm'}>
                        You have unsaved changes
                      </span>
                    )}
                  </div>
                </SheetFooter>
              </>
            )
          }}
        </EditGuidelineForm>
      </SheetContent>
    </Sheet>
  )
}
